
import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TicketFilter } from "@/lib/types/support-tickets";
import { Search, X, Filter } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

interface TicketFiltersProps {
  filters: TicketFilter;
  onFilterChange: (filters: TicketFilter) => void;
}

const TicketFilters: React.FC<TicketFiltersProps> = ({ 
  filters, 
  onFilterChange 
}) => {
  const [searchQuery, setSearchQuery] = React.useState(filters.searchQuery || "");
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  const handleSearch = () => {
    onFilterChange({
      ...filters,
      searchQuery
    });
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  const handleClearSearch = () => {
    setSearchQuery("");
    onFilterChange({
      ...filters,
      searchQuery: ""
    });
  };
  
  const handleStatusFilterChange = (statuses: string[]) => {
    onFilterChange({
      ...filters,
      status: statuses
    });
  };
  
  const handleTypeFilterChange = (types: string[]) => {
    onFilterChange({
      ...filters,
      type: types
    });
  };
  
  const handlePriorityFilterChange = (priorities: string[]) => {
    onFilterChange({
      ...filters,
      priority: priorities
    });
  };
  
  const handleClearFilters = () => {
    setSearchQuery("");
    onFilterChange({
      status: undefined,
      type: undefined,
      priority: undefined,
      searchQuery: ""
    });
  };
  
  const getFilterCountBadge = () => {
    let count = 0;
    if (filters.status && filters.status.length > 0) count++;
    if (filters.type && filters.type.length > 0) count++;
    if (filters.priority && filters.priority.length > 0) count++;
    return count > 0 ? count : undefined;
  };
  
  const filterCount = getFilterCountBadge();
  
  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-4">
      <div className="relative flex-grow">
        <Input
          placeholder="Search tickets..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          className="pr-8"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-8 top-0 h-full"
            onClick={handleClearSearch}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-full"
          onClick={handleSearch}
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>
      
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="relative flex gap-1 flex-shrink-0">
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {filterCount && (
              <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {filterCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div>
              <h4 className="font-section text-sm mb-2">Status</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-status-open" 
                    checked={filters.status?.includes('open')}
                    onCheckedChange={(checked) => {
                      const currentStatuses = filters.status || [];
                      const newStatuses = checked 
                        ? [...currentStatuses, 'open']
                        : currentStatuses.filter(s => s !== 'open');
                      handleStatusFilterChange(newStatuses);
                    }}
                  />
                  <Label htmlFor="filter-status-open">Open</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-status-in-progress" 
                    checked={filters.status?.includes('in_progress')}
                    onCheckedChange={(checked) => {
                      const currentStatuses = filters.status || [];
                      const newStatuses = checked 
                        ? [...currentStatuses, 'in_progress']
                        : currentStatuses.filter(s => s !== 'in_progress');
                      handleStatusFilterChange(newStatuses);
                    }}
                  />
                  <Label htmlFor="filter-status-in-progress">In Progress</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-status-pending-info" 
                    checked={filters.status?.includes('pending_info')}
                    onCheckedChange={(checked) => {
                      const currentStatuses = filters.status || [];
                      const newStatuses = checked 
                        ? [...currentStatuses, 'pending_info']
                        : currentStatuses.filter(s => s !== 'pending_info');
                      handleStatusFilterChange(newStatuses);
                    }}
                  />
                  <Label htmlFor="filter-status-pending-info">Pending Info</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-status-closed" 
                    checked={filters.status?.includes('closed')}
                    onCheckedChange={(checked) => {
                      const currentStatuses = filters.status || [];
                      const newStatuses = checked 
                        ? [...currentStatuses, 'closed']
                        : currentStatuses.filter(s => s !== 'closed');
                      handleStatusFilterChange(newStatuses);
                    }}
                  />
                  <Label htmlFor="filter-status-closed">Closed</Label>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-section text-sm mb-2">Type</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-type-feature" 
                    checked={filters.type?.includes('feature_request')}
                    onCheckedChange={(checked) => {
                      const currentTypes = filters.type || [];
                      const newTypes = checked 
                        ? [...currentTypes, 'feature_request']
                        : currentTypes.filter(t => t !== 'feature_request');
                      handleTypeFilterChange(newTypes);
                    }}
                  />
                  <Label htmlFor="filter-type-feature">Feature Request</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-type-error" 
                    checked={filters.type?.includes('error_report')}
                    onCheckedChange={(checked) => {
                      const currentTypes = filters.type || [];
                      const newTypes = checked 
                        ? [...currentTypes, 'error_report']
                        : currentTypes.filter(t => t !== 'error_report');
                      handleTypeFilterChange(newTypes);
                    }}
                  />
                  <Label htmlFor="filter-type-error">Error Report</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-type-update" 
                    checked={filters.type?.includes('update_request')}
                    onCheckedChange={(checked) => {
                      const currentTypes = filters.type || [];
                      const newTypes = checked 
                        ? [...currentTypes, 'update_request']
                        : currentTypes.filter(t => t !== 'update_request');
                      handleTypeFilterChange(newTypes);
                    }}
                  />
                  <Label htmlFor="filter-type-update">Update Request</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-type-suggestion" 
                    checked={filters.type?.includes('suggestion')}
                    onCheckedChange={(checked) => {
                      const currentTypes = filters.type || [];
                      const newTypes = checked 
                        ? [...currentTypes, 'suggestion']
                        : currentTypes.filter(t => t !== 'suggestion');
                      handleTypeFilterChange(newTypes);
                    }}
                  />
                  <Label htmlFor="filter-type-suggestion">Suggestion</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-type-general" 
                    checked={filters.type?.includes('general')}
                    onCheckedChange={(checked) => {
                      const currentTypes = filters.type || [];
                      const newTypes = checked 
                        ? [...currentTypes, 'general']
                        : currentTypes.filter(t => t !== 'general');
                      handleTypeFilterChange(newTypes);
                    }}
                  />
                  <Label htmlFor="filter-type-general">General</Label>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-section text-sm mb-2">Priority</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-priority-low" 
                    checked={filters.priority?.includes('low')}
                    onCheckedChange={(checked) => {
                      const currentPriorities = filters.priority || [];
                      const newPriorities = checked 
                        ? [...currentPriorities, 'low']
                        : currentPriorities.filter(p => p !== 'low');
                      handlePriorityFilterChange(newPriorities);
                    }}
                  />
                  <Label htmlFor="filter-priority-low">Low</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-priority-medium" 
                    checked={filters.priority?.includes('medium')}
                    onCheckedChange={(checked) => {
                      const currentPriorities = filters.priority || [];
                      const newPriorities = checked 
                        ? [...currentPriorities, 'medium']
                        : currentPriorities.filter(p => p !== 'medium');
                      handlePriorityFilterChange(newPriorities);
                    }}
                  />
                  <Label htmlFor="filter-priority-medium">Medium</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-priority-high" 
                    checked={filters.priority?.includes('high')}
                    onCheckedChange={(checked) => {
                      const currentPriorities = filters.priority || [];
                      const newPriorities = checked 
                        ? [...currentPriorities, 'high']
                        : currentPriorities.filter(p => p !== 'high');
                      handlePriorityFilterChange(newPriorities);
                    }}
                  />
                  <Label htmlFor="filter-priority-high">High</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="filter-priority-critical" 
                    checked={filters.priority?.includes('critical')}
                    onCheckedChange={(checked) => {
                      const currentPriorities = filters.priority || [];
                      const newPriorities = checked 
                        ? [...currentPriorities, 'critical']
                        : currentPriorities.filter(p => p !== 'critical');
                      handlePriorityFilterChange(newPriorities);
                    }}
                  />
                  <Label htmlFor="filter-priority-critical">Critical</Label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClearFilters}
              >
                Clear All
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TicketFilters;
